import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { getBooking, cancelBooking } from '../api/bookingApi';
import { formatDateTime } from '../utils/dateHelpers';
import './ManageBookingPage.css';
import StaffUnlock from '../components/StaffUnlock';
import { isStaffUnlocked } from '../utils/staffUnlock';

function ManageBookingPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const bookingId = searchParams.get('bookingId');

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isExpired, setIsExpired] = useState(false);

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // H5: Refs for focus management
    const cancelButtonRef = useRef(null);
    const modalFirstFocusRef = useRef(null);

    // M1: Update page title
    useEffect(() => {
        document.title = 'Your Appointment — The IMA Group';
    }, []);

    useEffect(() => {
        loadBooking();
    }, [bookingId]);

    // H5: Move focus into modal on open
    useEffect(() => {
        if (showCancelConfirm && modalFirstFocusRef.current) {
            modalFirstFocusRef.current.focus();
        }
    }, [showCancelConfirm]);

    // H5: Trap focus inside modal and close on Escape
    const handleModalKeyDown = (e) => {
        if (e.key === 'Escape') {
            closeCancelModal();
            return;
        }
        if (e.key !== 'Tab') return;

        const modal = e.currentTarget;
        const focusable = modal.querySelectorAll(
            'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    const closeCancelModal = () => {
        setShowCancelConfirm(false);
        // Restore focus to the button that opened the modal
        if (cancelButtonRef.current) {
            cancelButtonRef.current.focus();
        }
    };

    const loadBooking = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await getBooking(bookingId);

            if (response.is_expired && !isStaffUnlocked(bookingId)) {
                setIsExpired(true);
            } else if (response.success && response.booking) {
                setBooking(response.booking);
            } else {
                setError('Booking not found');
            }
        } catch (err) {
            setError('Failed to load booking details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        setActionLoading(true);
        setError('');

        try {
            const response = await cancelBooking(bookingId);

            if (response.success) {
                closeCancelModal();
                navigate(`/book?bookingId=${bookingId}`);
            } else {
                setError(response.error || 'Failed to cancel booking');
                closeCancelModal();
            }
        } catch (err) {
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Failed to cancel booking');
            } else {
                setError('An error occurred. Please try again.');
            }
            closeCancelModal();
        } finally {
            setActionLoading(false);
        }
    };

    const handleReschedule = () => {
        navigate(`/book?bookingId=${bookingId}&reschedule=true`);
    };

    if (loading) {
        return (
            <div className="manage-booking-page">
                {/* H3: role=status announces loading state to screen readers */}
                <div className="loading-container" role="status" aria-label="Loading your booking">
                    <div className="loading-spinner" aria-hidden="true"></div>
                    <p>Loading your booking...</p>
                </div>
            </div>
        );
    }

    if (isExpired) {
        return (
            <div className="manage-booking-page" style={{ textAlign: 'center', padding: '40px' }}>
                <div className="error-container" role="alert" style={{ margin: '0 auto', padding: '30px', maxWidth: '600px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 15px rgba(0,0,0,0.1)' }}>
                    <h2 style={{ color: '#d9534f', marginBottom: '15px' }}>Link Expired</h2>
                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '20px' }}>
                        This link has expired and is no longer available.<br/>
                        For help, please call us at <a href="tel:1-844-540-1882" style={{ color: '#0056b3', textDecoration: 'none', fontWeight: 'bold' }}>1-844-540-1882</a>.
                    </p>
                    <StaffUnlock
                        bookingId={bookingId}
                        onUnlock={() => {
                            setIsExpired(false);
                            loadBooking();
                        }}
                    />
                </div>
            </div>
        );
    }

    if (error && !booking) {
        return (
            <div className="manage-booking-page">
                {/* H3: role=alert announces error immediately */}
                <div className="error-container" role="alert">
                    <h2>No Valid Booking Link Found</h2>
                    <p>Please call <a href="tel:1-844-540-1882">1-844-540-1882</a> for concerns.</p>
                </div>
            </div>
        );
    }

    if (!booking) {
        return (
            <div className="manage-booking-page">
                <div className="no-booking-container">
                    <h2>No Booking Found</h2>
                    <p>You don't have an active appointment with this booking ID.</p>
                    <button onClick={() => navigate(`/book?bookingId=${bookingId}`)}>
                        Book a New Appointment
                    </button>
                </div>
            </div>
        );
    }

    const isCancelled = booking?.status === 'cancelled';
    const isPastAppointment = booking && new Date(booking.appointment_datetime) < new Date();

    return (
        <div className="manage-booking-page">
            <div className="page-header">
                <h1>{isCancelled ? 'Cancelled Appointment' : 'Your Appointment'}</h1>
            </div>

            {error && (
                /* H3: role=alert announces inline errors */
                <div className="error-banner" role="alert">
                    {error}
                </div>
            )}

            <div className="booking-details">
                <div className="details-section">
                    <h3>Appointment Information</h3>
                    <div className="detail-item">
                        <span className="label">Location:</span>
                        <span className="value">{booking.location}</span>
                    </div>
                    <div className="detail-item">
                        <span className="label">Date & Time:</span>
                        <span className="value">{formatDateTime(booking.appointment_datetime)}</span>
                    </div>
                    <div className="detail-item">
                        <span className="label">Status:</span>
                        <span className={`status ${booking.status}`}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                    </div>
                </div>

                <div className="details-section">
                    <h3>Contact Information</h3>
                    <div className="detail-item">
                        <span className="label">Name:</span>
                        <span className="value">{booking.first_name} {booking.last_name}</span>
                    </div>
                    {booking.email && booking.email !== 'none@noemail.local' && (
                        <div className="detail-item">
                            <span className="label">Email:</span>
                            <span className="value">{booking.email}</span>
                        </div>
                    )}
                    {booking.notes && (
                        <div className="detail-item">
                            <span className="label">Notes:</span>
                            <span className="value">{booking.notes}</span>
                        </div>
                    )}
                </div>

                {!isCancelled && (
                    <div className="action-buttons">
                        <button className="reschedule-button" onClick={handleReschedule}>
                            Reschedule Appointment
                        </button>
                        {!isPastAppointment && (
                            <button
                                ref={cancelButtonRef}
                                className="cancel-button"
                                onClick={() => setShowCancelConfirm(true)}
                                aria-haspopup="dialog"
                            >
                                Cancel Appointment
                            </button>
                        )}
                    </div>
                )}
            </div>

            {showCancelConfirm && (
                /* H5: Accessible modal dialog with focus trap */
                <div
                    className="modal-overlay"
                    onClick={closeCancelModal}
                >
                    <div
                        className="modal-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cancel-dialog-title"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleModalKeyDown}
                    >
                        <h3 id="cancel-dialog-title">Cancel Appointment?</h3>
                        <p>Are you sure you want to cancel your appointment?</p>
                        <p className="warning">
                            Note: Appointments must be cancelled at least 24 hours in advance.
                        </p>
                        <div className="modal-buttons">
                            <button
                                ref={modalFirstFocusRef}
                                className="confirm-cancel-button"
                                onClick={handleCancel}
                                disabled={actionLoading}
                            >
                                {actionLoading ? 'Cancelling...' : 'Yes, Cancel'}
                            </button>
                            <button
                                className="keep-button"
                                onClick={closeCancelModal}
                                disabled={actionLoading}
                            >
                                Keep Appointment
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageBookingPage;
